import { Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "../../../common/components/ui/table/TableFilters";
import Loading from "../../../common/components/ui/loading/Loading";
import GenericTable from "../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../common/components/ui/table/TableFooter";
import {
  IAutomaticRenewals,
  AUTO_RENEWAL_LOG_STATUS,
} from "@/types/AutomaticRenewals.type";
import { getAutomaticRenewals } from "@/common/services/AutomaticRenewals.api";
import { formatDateAndTime } from "@/common/helpers/date.helpers";

const STATUS_LABELS = {
  [AUTO_RENEWAL_LOG_STATUS.SUCCESS]: "Sucesso",
  [AUTO_RENEWAL_LOG_STATUS.FAILED]: "Falhou",
  [AUTO_RENEWAL_LOG_STATUS.SKIPPED]: "Ignorado",
};

const STATUS_COLORS = {
  [AUTO_RENEWAL_LOG_STATUS.SUCCESS]: "bg-greenPrimary text-white",
  [AUTO_RENEWAL_LOG_STATUS.FAILED]: "bg-redPrimary text-white",
  [AUTO_RENEWAL_LOG_STATUS.SKIPPED]: "bg-yellowPrimary",
};

function getStatusLabelComponent(status: AUTO_RENEWAL_LOG_STATUS) {
  return (
    <span
      className={`inline-block px-2 py-1 rounded-md text-sm whitespace-nowrap ${STATUS_COLORS[status] || "bg-gray-400"
        }`}
      title={STATUS_LABELS[status] || status}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

const headers = {
  renterName: "Nome",
  licensePlate: "Placa",
  status: "Status",
  createdAt: "Data",
  errorMessage: "Detalhes do Erro",
};

export default function AutomaticRenewalsTable() {
  const navigate = useNavigate();
  const { notifyError } = useToastContext();

  const [logs, setLogs] = useState<IAutomaticRenewals[]>([]);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    orderBy: "-createdAt",
  });

  async function fetchAutomaticRenewals(
    currentFilters: TableFiltersState,
    currentPage: number
  ) {
    setIsLoading(true);
    setLogs([]);
    const apiParams = {
      search: currentFilters.search,
      status: currentFilters.status,
      orderBy: currentFilters.orderBy,
      page: currentPage,
      itemsPerPage: data.itemsPerPage,
    };

    const result = await getAutomaticRenewals(apiParams);

    setIsLoading(false);

    if (!result.ok) {
      notifyError(
        (result as { message?: string })?.message ||
        "Erro desconhecido ao buscar logs."
      );
      return;
    }

    const mappedResult = (result.data || []).map((item) => ({
      ...item,
      renterName: item.renterInfo?.renterName ?? "-",
      licensePlate: item.vehicleInfo?.licensePlate ?? "-",
    }));

    setLogs(mappedResult);
    setData((prev) => ({ ...prev, totalItems: result.totalItems }));
  }

  useEffect(() => {
    fetchAutomaticRenewals(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Renovação Automática"
          placeholder="Digite o nome, CPF ou placa do veículo."
          newItemLabel=""
          onNewItemClick={() => { }}
          statusOptions={{
            [AUTO_RENEWAL_LOG_STATUS.SUCCESS]: "Sucesso",
            [AUTO_RENEWAL_LOG_STATUS.FAILED]: "Falhou",
            [AUTO_RENEWAL_LOG_STATUS.SKIPPED]: "Ignorado",
          }}
          onSearch={handleFilterChange}
          entity="automatic-renewals"
          tableData={logs}
        />

        <GenericTable
          headers={headers}
          data={logs}
          setFilters={({ orderBy }) => handleFilterChange({ orderBy })}
          renders={{
            createdAt: (date) => formatDateAndTime(date),
            renterName: (name, row) => (
              <a
                href={`/clientes/${(row as IAutomaticRenewals).renterInfo.renterId
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(
                    `/clientes/${(row as IAutomaticRenewals).renterInfo.renterId
                    }`
                  );
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {name as string}
              </a>
            ),
            licensePlate: (_plate, row) => (
              <a
                className="text-blue-600 hover:text-bluePrimary cursor-pointer"
                onClick={() =>
                  navigate(
                    `/veiculos/${(row as IAutomaticRenewals).vehicleInfo?.licensePlate
                    }`
                  )
                }
                target="_blank"
              >
                {(row as IAutomaticRenewals).vehicleInfo?.licensePlate}
              </a>
            ),
            status: (status) =>
              getStatusLabelComponent(status as AUTO_RENEWAL_LOG_STATUS),
          }}
          entity="automatic-renewals"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page}
          totalItems={data.totalItems}
          itemsPerPage={data.itemsPerPage}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}
